import {
  accountGeneration,
  accountScope,
  type Exercise,
  type TimedSessionResult,
} from "@heraion/shared";

import type {
  SessionResultsCacheModule,
  SessionResultsSnapshot,
} from "./types";

// Factory for a module-scope single-flight cache of the Progress
// session-results snapshot (perf B2). The shared `useSessionResults`
// hook is platform-agnostic, so the host injects an instance: web-rnw
// instantiates one of these at module scope; mobile passes its OWN
// equivalent module instead (so the save path can keep invalidating the
// same singleton without dragging AsyncStorage into useSaveWorkout's jest).
//
// #509: a snapshot older than this counts as absent for `ensure`: the next
// reader (Progress reopened, the app back in front) reads it again. A short
// expiry, not a poll: nothing runs while nobody asks.
export const SESSION_RESULTS_MAX_AGE_MS = 5 * 60 * 1000;

// Pure — no React, no storage. Concurrent callers (the panels mount
// together) await the same promise; a warm cache resolves immediately.
export function createSessionResultsCache(): SessionResultsCacheModule & {
  invalidate: () => void;
  isStale: () => boolean;
} {
  let cached: SessionResultsSnapshot | null = null;
  let cachedAt = 0;
  let inFlight: Promise<SessionResultsSnapshot> | null = null;
  const subscribers = new Set<() => void>();

  return {
    getCached: () => cached,
    isStale: () => cached !== null && Date.now() - cachedAt >= SESSION_RESULTS_MAX_AGE_MS,
    ensure: (fetcher) => {
      // Past the expiry `cached` stays readable (getCached, the paint under a
      // refresh) but is not served: the read goes again.
      if (cached && Date.now() - cachedAt < SESSION_RESULTS_MAX_AGE_MS) {
        return Promise.resolve(cached);
      }
      if (!inFlight) {
        // #397: a flight from before an account change (dropAccountCaches
        // moved the generation) answers its own callers, keeps nothing, and
        // leaves alone the flight the next account may have started.
        const scope = accountScope();
        const flight: Promise<SessionResultsSnapshot> = fetcher()
          .then((snap) => {
            if (!scope.current()) return snap;
            cached = snap;
            cachedAt = Date.now();
            if (inFlight === flight) inFlight = null;
            return snap;
          })
          .catch((e) => {
            if (inFlight === flight) inFlight = null;
            throw e;
          });
        inFlight = flight;
      }
      return inFlight;
    },
    subscribe: (fn) => {
      subscribers.add(fn);
      return () => {
        subscribers.delete(fn);
      };
    },
    // Drop the cache + notify mounted panels to re-fetch. Called from the
    // save path (a new workout recomputes its SessionResult server-side).
    invalidate: () => {
      cached = null;
      cachedAt = 0;
      inFlight = null;
      for (const fn of subscribers) fn();
    },
  };
}

/** #397: the stale (disk) copy of the snapshot, read once for the panels that
 *  mount together, not once each. Kept for one account: once the generation
 *  moves (dropAccountCaches) the next caller reads the disk again instead of
 *  the last account's copy. `read` is the app's own disk read (both parts, or
 *  null); a throw counts as no copy. `reset` drops the held read: a fresh
 *  fetch supersedes it. */
export function createStaleResultsReader(
  read: () => Promise<SessionResultsSnapshot | null>,
): { read: () => Promise<SessionResultsSnapshot | null>; reset: () => void } {
  let inFlight: Promise<SessionResultsSnapshot | null> | null = null;
  let generation = accountGeneration();
  return {
    read: () => {
      if (!inFlight || generation !== accountGeneration()) {
        generation = accountGeneration();
        inFlight = read().catch(() => null);
      }
      return inFlight;
    },
    reset: () => {
      inFlight = null;
    },
  };
}

/** #469: the hosts' disk copy of the newest rows, with the count the server
 *  sent beside them (`staleCovering` reads it). Under its own key
 *  (`PERSISTED_RESULTS_KEY`): a build from before #469 reads only the bare
 *  array under the old key, never this. */
export type PersistedResults = { results: TimedSessionResult[]; rawCount: number };

/** The key both hosts keep the copy under. `.v2` is the shape above; the old
 *  `progress/sessionResults` is left to older builds, never read: a copy is a
 *  cache, the next read online rebuilds it. */
export const PERSISTED_RESULTS_KEY = "progress/sessionResults.v2";

/** What a host writes: one place for the shape. */
export const persistedResultsOf = (
  results: TimedSessionResult[],
  rawCount: number,
): PersistedResults => ({ results, rawCount });

/** The stale snapshot from the copy and the cached catalog: both, or null. A
 *  copy of another shape (corrupt, hand-edited) is no copy. */
export function readPersistedResults(
  kept: PersistedResults | null,
  catalog: ReadonlyArray<Exercise> | null,
): SessionResultsSnapshot | null {
  if (!kept || !catalog || !Array.isArray(kept.results)) return null;
  return { results: kept.results, rawCount: kept.rawCount, catalog };
}
